import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Download, Key, Check, X } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Loan Agent", "Insurance Agent", "Other"];

export default function AdminAgents() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(null);
  const [pwNew, setPwNew] = useState("");
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name:"", email:"", phone:"", password:"", agent_type:"Loan Agent", pan:"", aadhaar_last4:"" });

  const load = async () => {
    const { data } = await api.get("/admin/agents");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name:"", email:"", phone:"", password:"", agent_type:"Loan Agent", pan:"", aadhaar_last4:"" }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, password: "" }); setOpen(true); };

  const approve = async (a) => {
    if (!window.confirm(`Approve ${a.name || a.code}? They will be able to sign in.`)) return;
    try { await api.post(`/admin/agents/${a.id}/approve`); toast.success(`Approved · ${a.code}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  };

  const reject = async (a) => {
    if (!window.confirm(`Reject ${a.name || a.code}? They will not be able to sign in.`)) return;
    try { await api.post(`/admin/agents/${a.id}/reject`); toast.success(`Rejected · ${a.code}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const visibleItems = filter === "all" ? items : items.filter(a => (a.status || "").toLowerCase() === filter);
  const pendingCount = items.filter(a => a.status === "Pending").length;

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/agents/${editing.id}`, { name: form.name, email: form.email, phone: form.phone, status: form.status || "Active", agent_type: form.agent_type });
        toast.success("Agent updated");
      } else {
        if (!form.password || form.password.length < 6) { toast.error("Password must be 6+ chars"); return; }
        const { data } = await api.post("/admin/agents", form);
        toast.success(`Agent created · code ${data.code}`);
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    await api.delete(`/admin/agents/${id}`); toast.success("Deleted"); load();
  };

  const resetPw = async () => {
    if (!pwNew || pwNew.length < 6) { toast.error("Password must be 6+ chars"); return; }
    try {
      await api.post(`/admin/agents/${pwOpen.id}/reset-password`, { new_password: pwNew });
      toast.success("Password reset"); setPwOpen(null); setPwNew("");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const exportCSV = async () => {
    const t = localStorage.getItem("tara_token");
    const res = await fetch(`${API_BASE}/admin/agents-export`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "tara_agents.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Network</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Agents</h1>
          <p className="text-sm text-ink-muted mt-1">Manage Loan / Insurance agents — codes auto-generated like <span className="font-mono">TFV00001</span>. Self-registrations land here as <strong>Pending</strong> until you approve them.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="agents-export" variant="outline" className="rounded-full" onClick={exportCSV}><Download className="h-4 w-4 mr-2"/>Export CSV</Button>
          <Button data-testid="agents-add" className="btn-saffron rounded-full" onClick={openNew}><Plus className="h-4 w-4 mr-2"/>Add agent</Button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="agents-status-filter">
        {[
          { key: "all", label: `All (${items.length})` },
          { key: "pending", label: `Pending (${pendingCount})`, highlight: pendingCount > 0 },
          { key: "active", label: "Active" },
          { key: "rejected", label: "Rejected" },
          { key: "suspended", label: "Suspended" },
        ].map(f => (
          <button key={f.key} type="button" data-testid={`agents-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
              ${filter === f.key ? "bg-navy text-saffron border-navy" : "bg-white text-ink-muted border-slate-200 hover:border-navy hover:text-navy"}
              ${f.highlight && filter !== f.key ? "ring-2 ring-saffron/40" : ""}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 card-flat p-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="agents-table">
          <thead>
            <tr className="text-left text-ink-muted border-b border-slate-200">
              <th className="py-2 pr-3">Code</th>
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Phone</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 && (<tr><td colSpan={7} className="py-10 text-center text-ink-muted">No agents in this filter.</td></tr>)}
            {visibleItems.map(a => {
              const statusClass = a.status === "Active" ? "bg-green-100 text-green-700"
                : a.status === "Pending" ? "bg-amber-100 text-amber-800"
                : a.status === "Rejected" ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-600";
              return (
              <tr key={a.id} className="border-b border-slate-100">
                <td className="py-3 pr-3 font-mono font-bold text-saffron">{a.code}</td>
                <td className="py-3 pr-3 font-medium">{a.name}</td>
                <td className="py-3 pr-3 text-xs">{a.email}</td>
                <td className="py-3 pr-3 font-mono text-xs">{a.phone}</td>
                <td className="py-3 pr-3">{a.agent_type}</td>
                <td className="py-3 pr-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>{a.status}</span>
                </td>
                <td className="py-3 pr-3 whitespace-nowrap">
                  {a.status === "Pending" && (
                    <>
                      <Button size="sm" variant="ghost" data-testid={`agent-approve-${a.code}`} onClick={() => approve(a)} title="Approve"><Check className="h-4 w-4 text-green-600"/></Button>
                      <Button size="sm" variant="ghost" data-testid={`agent-reject-${a.code}`} onClick={() => reject(a)} title="Reject"><X className="h-4 w-4 text-red-500"/></Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`agent-edit-${a.code}`} onClick={() => openEdit(a)}><Pencil className="h-4 w-4"/></Button>
                  <Button size="sm" variant="ghost" data-testid={`agent-pw-${a.code}`} onClick={() => setPwOpen(a)}><Key className="h-4 w-4"/></Button>
                  <Button size="sm" variant="ghost" data-testid={`agent-del-${a.code}`} onClick={() => remove(a.id)}><Trash2 className="h-4 w-4 text-red-500"/></Button>
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} agent</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input data-testid="ag-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
            <Field label="Email"><Input data-testid="ag-email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></Field>
            <Field label="Phone"><Input data-testid="ag-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></Field>
            <Field label="Agent type">
              <Select value={form.agent_type} onValueChange={(v)=>setForm({...form, agent_type:v})}>
                <SelectTrigger data-testid="ag-type"><SelectValue/></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {editing && (
              <Field label="Status">
                <Select value={form.status || "Active"} onValueChange={(v)=>setForm({...form, status:v})}>
                  <SelectTrigger data-testid="ag-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            {!editing && (
              <>
                <Field label="Password"><Input data-testid="ag-pw" type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})}/></Field>
                <Field label="PAN (optional)"><Input data-testid="ag-pan" value={form.pan} onChange={e=>setForm({...form, pan:e.target.value.toUpperCase()})}/></Field>
                <Field label="Aadhaar last 4 (optional)"><Input data-testid="ag-aad" value={form.aadhaar_last4} maxLength={4} onChange={e=>setForm({...form, aadhaar_last4:e.target.value})}/></Field>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button data-testid="ag-save" className="btn-saffron rounded-full" onClick={save}>{editing ? "Update" : "Create"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwOpen} onOpenChange={(v) => !v && setPwOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {pwOpen?.code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="New password (min 6 chars)"><Input data-testid="ag-pw-new" type="password" value={pwNew} onChange={e=>setPwNew(e.target.value)}/></Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={()=>{ setPwOpen(null); setPwNew(""); }}>Cancel</Button>
              <Button data-testid="ag-pw-save" className="btn-saffron rounded-full" onClick={resetPw}>Reset</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
